/** Global imports */

import cx from 'classnames';
import {useContext, useEffect, useRef} from 'react';
import {createPortal} from 'react-dom';
import {createRoot} from 'react-dom/client';
import {Typography} from '../../../atoms/Typography';
import {useMatchQuery} from '../../../hooks/useMatchQuery';
import {SearchModel} from '../../../models/SearchModel';
import {SearchSuggestionModel} from '../../../models/SearchSuggestionModel';
/** Local imports */
import {AutoCompleteSearchBar} from '../../../molecules/AutoCompleteSearchBar';
import {mediumAndUpQuery} from '../../../sass/base/_exports.sass';
import {TrendingIcon} from '../../../svg/TrendingIcon';
import {buildHref} from '../../../utils/utils';
import {useShouldShowInlineSearch} from '../utils';
import {SearchContext, SearchContextParent} from './SearchContext';
import './SearchWithCategory.sass';
import {isAllowedVideoFormat} from '~/common/fabric/utils';
import {Picture} from '~library/atoms/Picture';
import {Video} from '~library/atoms/Video';
import {useNewMegaNavExperiment} from '~library/organisms/MegaNav/useNewMegaNavExperiment';
import {getHeaderType} from '../../../utils/tracking';

/** Constants */
const customClassNames = {
  form: 'search-bar__form',
  input: 'search-bar__input',
  inputWrapperPrefix: 'search-bar__input--prefix-wrapper',
  inputWrapper: 'search-bar__input--wrapper',
  inputWrapperSuffix: 'search-bar__input--suffix-wrapper',
  list: 'search-bar__panel--list',
  item: 'search-bar__panel--item',
  panel: 'search-bar__panel',
  panelLayout: 'search-bar__panel--layout',
  sourceHeader: '',
  clearButton: 'search-bar__input--icons',
  submitButton: 'search-bar__input--icons',
  loadingIndicator: 'search-bar__input--icons',
  trendingIcon: 'search-bar__panel--item-icon-trending',
};
const MIN_SEARCH_SUGGESTION_QUERY_SIZE = 0;
const DEBOUNCING_DELAY_IN_MILLISECONDS = 400;
const CARD_FILTER = 'card';
const INVITATION_FILTER = 'invitation';

/** Helper components */
export const SuggestionItem = (props) => {
  const {displayName} = props;
  return (
    <div className="search-bar__panel--item-suggestion">
      <Typography variant="paragraph3" className="search-bar__panel--item-title">
        {displayName}
      </Typography>
      <button
        data-qa-id="search-bar__panel--item-button"
        className="search-bar__panel--item-button"
        type="button"
        title="Select"
        aria-label="Select"
      />
    </div>
  );
};

export const TemplateSuggestionItem = (props) => {
  const {id, displayName, src, data} = props;
  const isVideo = isAllowedVideoFormat(src);
  return (
    <div className="search-bar__panel--item-template" data-qa-id="search-bar__panel--item-template">
      {isVideo ? (
        <Video
          src={src}
          className="search-bar__panel--item-template-img"
          data-qa-id="search-bar__panel--item-template-img"
          inViewAutoPlay
        />
      ) : (
        <Picture
          srcSet={src}
          className="search-bar__panel--item-template-img"
          data-qa-id="search-bar__panel--item-template-img"
        />
      )}
      <Typography
        variant="paragraph3"
        className="search-bar__panel--item-template-title"
        data-qa-id="search-bar__panel--item-template-title"
      >
        {displayName}
      </Typography>
      <button
        data-qa-id="search-bar__panel--item-button"
        className="search-bar__panel--item-button"
        type="button"
        title="Select"
        data-ecom-category={`${data.premium ? 'Premium' : 'Free'}/${
          data.usedAs !== INVITATION_FILTER ? 'Card' : 'Invitation'
        }/${data.eventType}`}
        data-ecom-name={displayName}
        data-ecom-position="1"
        data-ecom-id={id}
        aria-label="Select"
      />
    </div>
  );
};

export const TrendingSuggestionItem = (props) => {
  const {displayName} = props;
  return (
    <div className="search-bar__panel--item-suggestion">
      <TrendingIcon ratio={0.7} className={customClassNames.trendingIcon} />
      <Typography variant="paragraph3" className="search-bar__panel--item-title">
        {displayName}
      </Typography>
      <button
        data-qa-id="search-bar__panel--item-button"
        className="search-bar__panel--item-button"
        type="button"
        title="Select"
        aria-label="Select"
      />
    </div>
  );
};

// Simple global click handler to close all autocomplete instances
const autocompleteInstances = [];

const closeAllAutocompleteInstances = () => {
  autocompleteInstances.forEach((instance) => {
    if (instance && typeof instance.setIsOpen === 'function') {
      instance.setIsOpen(false);
    }
  });
};

// Add global click listener only once
let globalClickListenerAdded = false;
if (!globalClickListenerAdded) {
  document.addEventListener('click', (event) => {
    // Check if click is outside any autocomplete container
    const clickedInsideAutocomplete = event.target.closest('[data-autocomplete-container]');
    if (!clickedInsideAutocomplete) {
      closeAllAutocompleteInstances();
    }
  });
  globalClickListenerAdded = true;
}

/** Main component */
export const SearchWithCategory = ({
  'data-qa-id': dataQaId = 'meganav-search',
  id = 'search-with-category',
  isInline,
  onSearchOpenClose,
}) => {
  const panelContainerId = `${id}-panel`;
  const {searchQuery, setSearchQuery} = useContext(SearchContext);
  const containerRef = useRef(null);
  const {inDesktopExperiment} = useNewMegaNavExperiment();
  const rounded = inDesktopExperiment;

  const {matches: mediumScreens} = useMatchQuery(`(${mediumAndUpQuery})`);

  // Register this instance for global click handling
  const cleanupRef = useRef(null);

  const handleInstanceCreated = (instance) => {
    autocompleteInstances.push(instance);
    cleanupRef.current = () => {
      // Clean up when component unmounts
      const index = autocompleteInstances.indexOf(instance);
      if (index > -1) {
        autocompleteInstances.splice(index, 1);
      }
    };
  };

  useEffect(() => {
    return () => {
      if (cleanupRef.current) {
        cleanupRef.current();
      }
    };
  }, []);

  /** mobx search model for memoizing the "getting suggestions" ajax calls */
  let searchModel = new SearchModel();

  /** useEffect hooks */
  // biome-ignore lint/correctness/useExhaustiveDependencies: existing functionality
  useEffect(() => {
    /** Populate search query (Invitations or Greeting Cards) based on URL params */

    // If the user has an in-progress search query (i.e. search container got moved to another spot on the page), just pick up where they left off
    if (searchQuery.current !== '') return;
    const urlParams = new URLSearchParams(window.location.search);
    const query = urlParams.get('query');
    const queryTypeExists = urlParams.has('queryType');
    if (query && !queryTypeExists) {
      setSearchQuery(query);
    }
  }, []);

  /** Debounce function for delaying sending suggestions to the backend * */
  const debouncePromise = (fn, time) => {
    let timerId;

    return function debounced(...args) {
      if (timerId) {
        clearTimeout(timerId);
      }

      return new Promise((resolve) => {
        timerId = setTimeout(() => resolve(fn(...args)), time);
      });
    };
  };
  const debounced = debouncePromise(
    (items) => Promise.resolve(items),
    DEBOUNCING_DELAY_IN_MILLISECONDS
  );

  const emptyQuery = (query) => !query || query.trim() === '';

  /** Handle clicking a search suggestion in the searchbar */
  const onSuggestionItemClick = (filter, item, context) => {
    const header = document.getElementById('header');
    const isAtTop = header ? Math.round(header.getBoundingClientRect().top) === 0 : false;

    const isHomepage = window.location.pathname === '/';
    const location = getHeaderType(isHomepage, isAtTop);

    const params = new URLSearchParams();
    params.append('searchBarLocation', location);
    params.append('searchBarContext', context);
    const searchFilterParam = filter || INVITATION_FILTER;
    params.append('filter', searchFilterParam);
    if (item.type === SearchSuggestionModel.TYPE_CATEGORY) {
      window.location.href = `${window.location.origin}/gallery/category/${item.id}/?${params.toString()}`;
    } else if (item.type === SearchSuggestionModel.TYPE_TEMPLATE) {
      const url = buildHref(item.data, searchModel.tracking, 1);
      window.location.href = `${url}&searchBarLocation=${location}&searchBarContext=${context}`;
    } else if (item.type === SearchSuggestionModel.TYPE_SUGGESTION) {
      params.append('query', item.id);
      window.location.href = `${window.location.origin}/gallery/search/?${params.toString()}`;
    }
  };

  /** Get search term and category suggestions */
  const getSuggestionsAndCategoriesFromQuery = async (query) => {
    const model = searchModel;
    let invitation = {};
    let card = {};
    try {
      const filters = [INVITATION_FILTER, CARD_FILTER];
      const json = await model.fetchSearchTerm(query, filters);
      if (json && (query === json.search || (!query && !json.search))) {
        await model.sync(json);
      }
      invitation = model.invitation;
      card = model.card;
      searchModel = model;
    } catch (err) {
      evite.error(err);
    }
    setSearchQuery(query);
    return {
      invitation,
      card,
    };
  };

  /** Sources */
  const suggestionsSource = (
    filter,
    query,
    title,
    searchType,
    innerOnSuggestionItemClick,
    context
  ) => ({
    sourceId: `${filter}-${searchType}`,
    getItems() {
      return getSuggestionsAndCategoriesFromQuery(query).then((result) => {
        if (searchType === SearchSuggestionModel.TYPE_CATEGORY) {
          return result[filter].categories;
        }
        if (searchType === SearchSuggestionModel.TYPE_TEMPLATE) {
          return result[filter].templates;
        }
        return result[filter].suggestions;
      });
    },
    templates: {
      header({items}) {
        if (items.length === 0 || !title) {
          return null;
        }
        return (
          <Typography variant="paragraph4" className="search-bar__panel--source-header">
            {title}
          </Typography>
        );
      },
      item({item}) {
        const displayName = item.text;
        if (emptyQuery(query)) {
          return <TrendingSuggestionItem displayName={displayName} />;
        }
        if (searchType === SearchSuggestionModel.TYPE_SUGGESTION) {
          return <SuggestionItem displayName={displayName} />;
        }
        if (searchType === SearchSuggestionModel.TYPE_TEMPLATE) {
          return (
            <TemplateSuggestionItem
              id={item.id}
              displayName={displayName}
              src={item.src}
              data={item.data}
            />
          );
        }
        return null;
      },
    },
    onSelect({item}) {
      return innerOnSuggestionItemClick(filter, item, context);
    },
  });

  const determineIfInline = (key) => customClassNames[key].concat(isInline ? ' inline' : '');
  const productsSuggestionTitle = (filter) =>
    filter === CARD_FILTER ? 'Greeting Cards' : 'Invitations';
  const header = document.getElementById('header');
  const isAtTop = header ? Math.round(header.getBoundingClientRect().top) === 0 : false;

  const isHomepage = window.location.pathname === '/';
  const searchBarLocation = getHeaderType(isHomepage, isAtTop);

  return (
    <div
      className={`search-with-category ${isInline ? 'inline' : ''}`}
      id={id}
      data-autocomplete-container
    >
      <div className="search-bar__wrapper">
        <AutoCompleteSearchBar
          data-qa-id={dataQaId}
          searchBarLocation={searchBarLocation}
          initialState={{query: searchQuery.current}}
          onInstanceCreated={handleInstanceCreated}
          classNames={{
            ...customClassNames,
            form: cx(determineIfInline('form'), {rounded}),
            inputWrapperPrefix: determineIfInline('inputWrapperPrefix'),
            inputWrapperSuffix: determineIfInline('inputWrapperSuffix'),
            inputWrapper: determineIfInline('inputWrapper'),
            submitButton: cx(determineIfInline('submitButton'), {rounded}),
          }}
          panelContainer={`#${panelContainerId}`}
          openOnFocus
          onSubmit={({state}) => {
            if (state.query === '' || state.query === null) return;
            onSuggestionItemClick(
              state.query.includes(' card') ? CARD_FILTER : INVITATION_FILTER,
              new SearchSuggestionModel({
                id: state.query,
                text: state.query,
                type: SearchSuggestionModel.TYPE_SUGGESTION,
              }),
              'manual_search'
            );
          }}
          placeholder={`Search designs${isInline ? ', themes or categories' : ''}`}
          getSources={async ({query}) => {
            const formattedQuery = query?.toLowerCase();
            if (formattedQuery?.split(' ').join('').length < MIN_SEARCH_SUGGESTION_QUERY_SIZE) {
              return [];
            }
            if (mediumScreens) {
              return debounced([
                suggestionsSource(
                  INVITATION_FILTER,
                  formattedQuery,
                  emptyQuery(query) ? '' : productsSuggestionTitle(INVITATION_FILTER),
                  SearchSuggestionModel.TYPE_SUGGESTION,
                  onSuggestionItemClick,
                  emptyQuery(query) ? 'raw_suggestion' : 'suggestion'
                ),
                suggestionsSource(
                  INVITATION_FILTER,
                  formattedQuery,
                  '',
                  SearchSuggestionModel.TYPE_TEMPLATE,
                  onSuggestionItemClick,
                  'template'
                ),
                suggestionsSource(
                  CARD_FILTER,
                  formattedQuery,
                  emptyQuery(query) ? '' : productsSuggestionTitle(CARD_FILTER),
                  SearchSuggestionModel.TYPE_SUGGESTION,
                  onSuggestionItemClick,
                  'suggestion'
                ),
              ]);
            }
            return debounced([
              suggestionsSource(
                INVITATION_FILTER,
                formattedQuery,
                emptyQuery(query) ? '' : productsSuggestionTitle(INVITATION_FILTER),
                SearchSuggestionModel.TYPE_SUGGESTION,
                onSuggestionItemClick,
                emptyQuery(query) ? 'raw_suggestion' : 'suggestion'
              ),
              suggestionsSource(
                CARD_FILTER,
                formattedQuery,
                emptyQuery(query) ? '' : productsSuggestionTitle(CARD_FILTER),
                SearchSuggestionModel.TYPE_SUGGESTION,
                onSuggestionItemClick,
                'suggestion'
              ),
            ]);
          }}
          ref={containerRef}
          onStateChange={({state, prevState}) => {
            // Only call when isOpen actually changes to avoid unnecessary re-renders
            if (typeof onSearchOpenClose === 'function' && state.isOpen !== prevState.isOpen) {
              onSearchOpenClose(state.isOpen);
            }
          }}
        />
        <div className="search-bar__suggestion-dropdown" id={panelContainerId} />
      </div>
    </div>
  );
};

export const SearchWithCategoryWrapper = () => (
  <SearchContextParent>
    <SearchWithCategory />
  </SearchContextParent>
);

export const SearchWithCategoryPortal = () => {
  const showInline = useShouldShowInlineSearch();
  const elem = document.getElementById(showInline ? 'inline-search' : 'meganav-search');
  const {inMobileExperiment} = useNewMegaNavExperiment();

  if (inMobileExperiment) {
    return null;
  }

  return elem ? createPortal(<SearchWithCategory isInline={showInline} />, elem) : null;
};

const container = document.getElementById('search-container');
if (container) {
  const root = createRoot(container);
  root.render(
    <SearchContextParent>
      <SearchWithCategoryPortal />
    </SearchContextParent>
  );
}
